/* ===================================================
   SUPATEA — Editorial storefront behaviour
   Vanilla JS, no dependencies. Every interaction here
   degrades to a working non-JS path:
     - add to cart falls back to a native form POST
     - mobile nav falls back to the footer/site links
     - search falls back to /search
   =================================================== */

(function () {
  'use strict';

  /**
   * Sections inside a section group are addressed by their full generated id
   * (e.g. "sections--20570525761705__spt-cart-drawer"), not the short handle.
   * Requesting the short name makes the Section Rendering API return null and
   * the drawer silently keeps stale contents, so the id is read from the DOM.
   */
  var CART_DRAWER_SECTION = 'spt-cart-drawer';
  function drawerSectionId(drawer) {
    if (!drawer) return CART_DRAWER_SECTION;
    if (drawer.dataset.sectionId) return drawer.dataset.sectionId;
    var wrap = drawer.closest('.shopify-section');
    if (wrap && wrap.id) return wrap.id.replace(/^shopify-section-/, '');
    return CART_DRAWER_SECTION;
  }

  /* ---------- helpers ---------------------------- */

  function $(sel, root) {
    return (root || document).querySelector(sel);
  }
  function $$(sel, root) {
    return Array.prototype.slice.call((root || document).querySelectorAll(sel));
  }

  /**
   * The theme editor fires shopify:section:load on every edit, which re-runs
   * init(). This keeps listeners from stacking up on elements that survive.
   */
  function once(el, key) {
    if (el.dataset[key] === 'true') return false;
    el.dataset[key] = 'true';
    return true;
  }

  var FOCUSABLE =
    'a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), ' +
    'select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

  /**
   * Minimal accessible overlay controller: scroll lock, focus trap,
   * Escape to close, and focus restoration to the trigger.
   */
  function Overlay(root, panelSelector) {
    this.root = root;
    this.panel = $(panelSelector, root);
    this.lastFocused = null;
    this.onKeydown = this.handleKeydown.bind(this);
  }

  Overlay.prototype.isOpen = function () {
    return this.root.getAttribute('data-open') === 'true';
  };

  Overlay.prototype.open = function (trigger) {
    if (this.isOpen()) return;
    this.lastFocused = trigger || document.activeElement;
    this.root.setAttribute('data-open', 'true');
    document.addEventListener('keydown', this.onKeydown);

    var self = this;
    window.requestAnimationFrame(function () {
      var first = $(FOCUSABLE, self.panel);
      if (first) first.focus();
    });
  };

  Overlay.prototype.close = function () {
    if (!this.isOpen()) return;
    this.root.setAttribute('data-open', 'false');
    document.removeEventListener('keydown', this.onKeydown);

    if (this.lastFocused && document.contains(this.lastFocused)) {
      this.lastFocused.focus();
    }
    this.lastFocused = null;
  };

  Overlay.prototype.handleKeydown = function (event) {
    if (event.key === 'Escape') {
      event.preventDefault();
      this.close();
      return;
    }
    if (event.key !== 'Tab') return;

    var items = $$(FOCUSABLE, this.panel).filter(function (el) {
      return el.offsetParent !== null;
    });
    if (!items.length) return;

    var first = items[0];
    var last = items[items.length - 1];

    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  };

  /* ---------- header ----------------------------- */

  function initHeader() {
    var header = $('[data-spt-header]');
    if (!header || !once(header, 'sptBound')) return;

    // Compact-on-scroll (opt in from the section schema)
    if (header.getAttribute('data-sticky') === 'true') {
      var ticking = false;
      var apply = function () {
        header.setAttribute('data-compact', window.scrollY > 90 ? 'true' : 'false');
        ticking = false;
      };
      apply();
      window.addEventListener(
        'scroll',
        function () {
          if (ticking) return;
          ticking = true;
          window.requestAnimationFrame(apply);
        },
        { passive: true }
      );
    }

    // Search reveal
    var searchToggle = $('[data-search-toggle]', header);
    var searchPanel = $('[data-search-panel]', header);
    if (searchToggle && searchPanel) {
      searchToggle.addEventListener('click', function () {
        var open = searchPanel.getAttribute('data-open') === 'true';
        searchPanel.setAttribute('data-open', open ? 'false' : 'true');
        searchToggle.setAttribute('aria-expanded', open ? 'false' : 'true');
        if (!open) {
          var input = $('input[type="search"], input[name="q"]', searchPanel);
          if (input) input.focus();
        }
      });
      searchPanel.addEventListener('keydown', function (event) {
        if (event.key === 'Escape') {
          searchPanel.setAttribute('data-open', 'false');
          searchToggle.setAttribute('aria-expanded', 'false');
          searchToggle.focus();
        }
      });
    }

    // Mobile navigation drawer
    var navRoot = $('[data-nav-drawer]');
    if (navRoot) {
      var nav = new Overlay(navRoot, '.spt-nav-drawer__panel');

      $$('[data-nav-open]').forEach(function (btn) {
        btn.addEventListener('click', function () {
          nav.open(btn);
        });
      });
      $$('[data-nav-close]', navRoot).forEach(function (btn) {
        btn.addEventListener('click', function () {
          nav.close();
        });
      });

      // Collapsible sub-menus
      $$('[data-nav-toggle]', navRoot).forEach(function (btn) {
        var panel = btn.nextElementSibling;
        if (!panel) return;
        panel.hidden = true;
        btn.setAttribute('aria-expanded', 'false');
        btn.addEventListener('click', function () {
          var open = btn.getAttribute('aria-expanded') === 'true';
          btn.setAttribute('aria-expanded', open ? 'false' : 'true');
          panel.hidden = open;
        });
      });
    }
  }

  /* ---------- cart ------------------------------- */

  var cart = {
    drawer: null,
    overlay: null,

    init: function () {
      var drawer = $('[data-cart-drawer]');
      if (drawer && (drawer !== this.drawer || !this.overlay)) {
        this.drawer = drawer;
        this.overlay = new Overlay(drawer, '.spt-drawer__panel');
        if (once(drawer, 'sptBound')) this.bindDrawer();
      }
      this.bindTriggers();
      this.bindForms();
    },

    /** Drawer chrome: close buttons + delegated line-item quantity controls. */
    bindDrawer: function () {
      var self = this;

      this.drawer.addEventListener('click', function (event) {
        if (event.target.closest('[data-drawer-close]')) {
          event.preventDefault();
          self.overlay.close();
          return;
        }
        var changer = event.target.closest('[data-line-change]');
        if (changer) {
          event.preventDefault();
          self.changeLine(changer.dataset.key, changer.dataset.qty);
        }
      });
    },

    /** Bag icon opens the drawer instead of navigating, when JS is available. */
    bindTriggers: function () {
      var self = this;
      if (!this.drawer) return;

      $$('[data-cart-open]').forEach(function (el) {
        if (!once(el, 'sptBound')) return;
        el.addEventListener('click', function (event) {
          event.preventDefault();
          self.overlay.open(el);
        });
      });
    },

    bindForms: function () {
      var self = this;
      $$('form[data-spt-add-form]').forEach(function (form) {
        if (!once(form, 'sptBound')) return;
        form.addEventListener('submit', function (event) {
          event.preventDefault();
          self.add(form);
        });
      });
    },

    setBusy: function (state) {
      if (this.drawer) this.drawer.setAttribute('data-busy', state ? 'true' : 'false');
    },

    /** Swap the server-rendered drawer body and sync every cart badge. */
    render: function (sections) {
      if (!this.drawer || !sections) return;
      var html = sections[drawerSectionId(this.drawer)] || sections[CART_DRAWER_SECTION];
      if (!html) return;

      var parsed = new DOMParser().parseFromString(html, 'text/html');
      var next = $('[data-drawer-refresh]', parsed);
      var current = $('[data-drawer-refresh]', this.drawer);
      if (!next || !current) return;

      current.innerHTML = next.innerHTML;

      var state = $('[data-cart-state]', current);
      if (state) this.setCount(parseInt(state.getAttribute('data-cart-state'), 10) || 0);
    },

    setCount: function (count) {
      $$('[data-cart-count]').forEach(function (el) {
        el.textContent = count;
        el.setAttribute('data-empty', count > 0 ? 'false' : 'true');
      });
      var label = $('[data-drawer-count]');
      if (label) label.textContent = '(' + count + ')';
    },

    add: function (form) {
      var self = this;
      var button = form.querySelector('[type="submit"]');
      var errorBox = form.querySelector('[data-cart-error]');

      // Guard against double submission while a request is in flight.
      if (form.dataset.submitting === 'true') return;
      form.dataset.submitting = 'true';

      if (errorBox) errorBox.textContent = '';
      if (button) {
        button.setAttribute('data-loading', 'true');
        button.setAttribute('aria-disabled', 'true');
      }

      var body = new FormData(form);
      body.append('sections', drawerSectionId(self.drawer));
      body.append('sections_url', window.location.pathname);

      fetch(window.Shopify.routes.root + 'cart/add.js', {
        method: 'POST',
        headers: { Accept: 'application/json' },
        body: body
      })
        .then(function (response) {
          return response.json().then(function (data) {
            return { ok: response.ok, data: data };
          });
        })
        .then(function (result) {
          if (!result.ok) {
            throw new Error(result.data.description || result.data.message || 'This item could not be added.');
          }
          self.render(result.data.sections);
          if (self.overlay) self.overlay.open(button);
        })
        .catch(function (error) {
          if (errorBox) {
            errorBox.textContent = error.message || 'Something went wrong. Please try again.';
          } else {
            // No inline slot available — fall back to the real cart page.
            form.submit();
          }
        })
        .finally(function () {
          form.dataset.submitting = 'false';
          if (button) {
            button.removeAttribute('data-loading');
            button.removeAttribute('aria-disabled');
          }
        });
    },

    changeLine: function (key, quantity) {
      var self = this;
      this.setBusy(true);

      fetch(window.Shopify.routes.root + 'cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          id: key,
          quantity: parseInt(quantity, 10),
          sections: drawerSectionId(self.drawer),
          sections_url: window.location.pathname
        })
      })
        .then(function (response) {
          return response.json();
        })
        .then(function (data) {
          self.render(data.sections);
          if (typeof data.item_count === 'number') self.setCount(data.item_count);
        })
        .catch(function () {
          // Quantity is authoritative on the server — reload rather than guess.
          window.location.reload();
        })
        .finally(function () {
          self.setBusy(false);
        });
    }
  };

  /* ---------- featured product variant picker ----- */

  /**
   * Keeps the hidden variant id, price and availability in sync with the
   * selected options. Variant data is printed as JSON by the section so no
   * extra request is needed.
   */
  function initVariantPickers() {
    $$('[data-variant-picker]').forEach(function (picker) {
      if (!once(picker, 'sptBound')) return;
      var dataEl = $('[data-variant-json]', picker);
      if (!dataEl) return;

      var variants;
      try {
        variants = JSON.parse(dataEl.textContent);
      } catch (error) {
        return;
      }

      var idInput = $('[data-variant-id]', picker);
      var priceEl = $('[data-variant-price]', picker);
      var button = $('[data-add-button]', picker);
      var buttonText = button ? $('[data-add-button-text]', button) : null;

      /**
       * Products with a single default variant render no radios. Matching an
       * empty selection against ["Default Title"] never succeeds, which used to
       * force the button to "Unavailable" on every single-variant product —
       * so in that case the server-rendered state is already correct.
       */
      var radios = $$('input[type="radio"]', picker);
      if (!radios.length) return;

      function selected() {
        return $$('input[type="radio"]:checked', picker).map(function (input) {
          return input.value;
        });
      }

      function update() {
        var chosen = selected();
        var match = variants.find(function (variant) {
          return variant.options.every(function (option, index) {
            return option === chosen[index];
          });
        });

        if (!match) {
          if (button) {
            button.setAttribute('aria-disabled', 'true');
            if (buttonText) buttonText.textContent = 'Unavailable';
          }
          return;
        }

        if (idInput) idInput.value = match.id;
        if (priceEl) priceEl.innerHTML = match.price_html;

        if (button) {
          if (match.available) {
            button.removeAttribute('aria-disabled');
            button.disabled = false;
            if (buttonText) {
              buttonText.textContent = button.dataset.labelAvailable + ' — ' + match.price_plain;
            }
          } else {
            button.setAttribute('aria-disabled', 'true');
            button.disabled = true;
            if (buttonText) buttonText.textContent = button.dataset.labelSoldOut || 'Sold out';
          }
        }
      }

      $$('input[type="radio"]', picker).forEach(function (input) {
        input.addEventListener('change', update);
      });

      update();
    });
  }

  /* ---------- boot -------------------------------- */

  function init() {
    // Shopify.routes is present on the storefront; guard for the editor preview.
    window.Shopify = window.Shopify || {};
    window.Shopify.routes = window.Shopify.routes || { root: '/' };

    initHeader();
    initVariantPickers();
    cart.init();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Re-bind when the theme editor re-renders a section.
  document.addEventListener('shopify:section:load', init);
})();
